import { useEffect, useState } from "react";

interface ParallaxBannerProps {
  image: string;
  title: string;
  subtitle?: string;
  height?: string;
}

export const ParallaxBanner = ({ 
  image, 
  title, 
  subtitle,
  height = "70vh" 
}: ParallaxBannerProps) => {
  const [scrollY, setScrollY] = useState(0);

  useEffect(() => {
    const handleScroll = () => {
      setScrollY(window.scrollY);
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <div 
      className="relative w-screen left-[50%] right-[50%] -mx-[50vw] overflow-hidden"
      style={{ height }}
    >
      {/* Parallax Background Image */}
      <div 
        className="absolute inset-0 w-full h-[120%] -top-[10%]"
        style={{
          transform: `translateY(${scrollY * 0.3}px)`,
          willChange: "transform"
        }}
      >
        <img
          src={image}
          alt={title}
          className="w-full h-full object-cover"
        />
        {/* Dark overlay for text readability */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-black/60" />
      </div>

      {/* Content */}
      <div className="relative h-full flex flex-col items-center justify-center text-center px-6 z-10">
        <div 
          className="max-w-4xl space-y-4"
          style={{
            transform: `translateY(${scrollY * 0.2}px)`,
            opacity: Math.max(0, 1 - scrollY / 400),
            willChange: "transform, opacity"
          }}
        >
          <h1 className="text-5xl md:text-7xl lg:text-8xl font-bold text-white drop-shadow-2xl animate-fade-in">
            {title}
          </h1>
          {subtitle && (
            <p className="text-xl md:text-2xl lg:text-3xl text-white/90 drop-shadow-lg animate-fade-in-slow">
              {subtitle}
            </p>
          )}
        </div>
      </div>

      {/* Scroll indicator */}
      <div 
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10"
        style={{
          opacity: Math.max(0, 1 - scrollY / 200),
          willChange: "opacity"
        }}
      >
        <div className="flex flex-col items-center gap-2 animate-bounce">
          <span className="text-white/80 text-sm">Scroll to explore</span>
          <svg 
            className="w-6 h-6 text-white/80" 
            fill="none" 
            strokeLinecap="round" 
            strokeLinejoin="round" 
            strokeWidth="2" 
            viewBox="0 0 24 24" 
            stroke="currentColor"
          >
            <path d="M19 14l-7 7m0 0l-7-7m7 7V3"></path>
          </svg>
        </div>
      </div>
    </div>
  );
};
